/**
 * PURE trash logic (UNDO-TRASH-LIGHT L2) — no Solid or PowerSync dependencies.
 *
 * Trash unifies THREE classes of soft-deleted entities (`deleted_at`):
 *   - notas (`bible_note`),
 *   - highlights (`bible_verse_highlight` rows grouped by origin group
 *     `bible_highlight`, same grouping as /highlights),
 *   - marcadores (`bible_bookmark`).
 *
 * These functions are deterministic and unit-testable: they group deleted
 * flat highlight rows and merge the three classes into a single list
 * sorted by deletion date (most recent first).
 */
import type { VerseRef } from "@/utils/verse-reference";
import { compareVerseRefs } from "@/utils/verse-collections";

export interface TrashedNoteEntry {
  kind: "note";
  id: string;
  textContent: string | null;
  deletedAt: string | null;
}

export interface TrashedHighlightEntry {
  kind: "highlight";
  /** Group key: `origin_id` if present, otherwise orphan row id. */
  id: string;
  /** Origin group (`bible_highlight.id`) or null for legacy loose rows. */
  originId: string | null;
  /** Flat `bible_verse_highlight` rows of group (restore/purge covers ALL). */
  rowIds: string[];
  color: string;
  deletedAt: string | null;
  /** Group verses in canonical order. */
  verses: (VerseRef & { version: string })[];
}

export interface TrashedBookmarkEntry {
  kind: "bookmark";
  id: string;
  deletedAt: string | null;
  version: string;
  book: number;
  chapter: number;
  verse: number;
}

export type TrashEntry = TrashedNoteEntry | TrashedHighlightEntry | TrashedBookmarkEntry;

/** Flat deleted `bible_verse_highlight` row, directly from query. */
export interface TrashedHighlightRow {
  highlightId: string | null;
  originId: string | null;
  color: string | null;
  deletedAt: string | null;
  version: string | null;
  book: number | null;
  chapter: number | null;
  verse: number | null;
}

const DEFAULT_HIGHLIGHT_COLOR = "#eab308";

/**
 * Groups deleted `bible_verse_highlight` rows by origin group
 * (`origin_id`; fallback: row itself), matching /highlights behavior for
 * live rows. Group `deletedAt` is MOST RECENT of its rows (all are
 * deleted together, but for disparate legacy data latest wins).
 */
export function groupTrashedHighlightRows(rows: TrashedHighlightRow[]): TrashedHighlightEntry[] {
  const groups = new Map<string, TrashedHighlightEntry>();

  for (const row of rows) {
    if (!row.highlightId) {
      continue;
    }
    const key = row.originId ?? row.highlightId;
    let group = groups.get(key);
    if (!group) {
      group = {
        kind: "highlight",
        id: key,
        originId: row.originId,
        rowIds: [],
        color: row.color ?? DEFAULT_HIGHLIGHT_COLOR,
        deletedAt: row.deletedAt,
        verses: [],
      };
      groups.set(key, group);
    }
    group.rowIds.push(row.highlightId);
    if (row.deletedAt && (!group.deletedAt || row.deletedAt > group.deletedAt)) {
      group.deletedAt = row.deletedAt;
    }
    if (row.book !== null && row.chapter !== null && row.verse !== null) {
      group.verses.push({
        version: row.version ?? "kjv",
        book: row.book,
        chapter: row.chapter,
        verse: row.verse,
      });
    }
  }

  const list = [...groups.values()];
  for (const group of list) {
    group.verses.sort(compareVerseRefs);
  }
  return list;
}

/**
 * Merges three classes into single list sorted by descending `deletedAt`
 * (most recently deleted at top); entries without date go to
 * the end. Ties: stable order by id for deterministic rendering.
 */
export function mergeTrashEntries(
  notes: TrashedNoteEntry[],
  highlights: TrashedHighlightEntry[],
  bookmarks: TrashedBookmarkEntry[],
): TrashEntry[] {
  const all: TrashEntry[] = [...notes, ...highlights, ...bookmarks];
  return all.toSorted((a, b) => {
    if (a.deletedAt && b.deletedAt && a.deletedAt !== b.deletedAt) {
      return b.deletedAt.localeCompare(a.deletedAt);
    }
    if (Boolean(a.deletedAt) !== Boolean(b.deletedAt)) {
      return a.deletedAt ? -1 : 1;
    }
    return a.id.localeCompare(b.id);
  });
}
